import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PartnerService } from '../../services/partner.service';
import { PartnerInvoiceDto, getInvoiceStatusLabel, getInvoiceStatusClass } from '../../models/invoice.model';

@Component({
    selector: 'app-partner-invoices',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './partner-invoices.component.html',
    styleUrl: './partner-invoices.component.css'
})
export class PartnerInvoicesComponent implements OnInit {
    invoices: PartnerInvoiceDto[] = [];
    isLoading: boolean = false;
    errorMessage: string = '';

    // Pagination state
    totalCount: number = 0;
    totalPages: number = 0;
    currentPage: number = 1;
    pageIndex: number = 0;
    pageSize: number = 20;

    // Expose Math to template
    Math = Math;

    constructor(private partnerService: PartnerService) { }

    ngOnInit(): void {
        this.loadInvoices();
    }

    /**
     * Load invoices from API
     */
    loadInvoices(): void {
        this.isLoading = true;
        this.errorMessage = '';

        this.partnerService.getInvoices(this.pageIndex, this.pageSize).subscribe({
            next: (response) => {
                this.invoices = response.items;
                this.totalCount = response.totalCount;
                this.currentPage = response.pageNumber + 1;
                this.totalPages = Math.ceil(response.totalCount / response.pageSize);
                this.isLoading = false;
            },
            error: (error) => {
                this.isLoading = false;
                this.errorMessage = error.error?.message || 'Failed to load invoices. Please try again.';
                console.error('Error loading invoices:', error);
            }
        });
    }

    /**
     * Get status label for display
     */
    getStatusLabel(status?: string): string {
        return getInvoiceStatusLabel(status);
    }

    /**
     * Get status CSS class
     */
    getStatusClass(status?: string): string {
        return getInvoiceStatusClass(status);
    }

    /**
     * Format date for display
     */
    formatDate(dateString?: string): string {
        if (!dateString) return 'N/A';

        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    }

    /**
     * Format date range for billing period
     */
    formatDateRange(startDate?: string, endDate?: string): string {
        if (!startDate || !endDate) return 'N/A';

        const start = new Date(startDate);
        const end = new Date(endDate);

        const startFormatted = start.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short'
        });

        const endFormatted = end.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });

        return `${startFormatted} - ${endFormatted}`;
    }

    /**
     * Download invoice
     */
    downloadInvoice(invoice: PartnerInvoiceDto, event: Event): void {
        if (invoice.invoiceDownloadUrl) {
            window.open(invoice.invoiceDownloadUrl, '_blank');
        }
    }

    /**
     * Refresh invoices
     */
    refreshInvoices(): void {
        this.loadInvoices();
    }

    /**
     * Go to a specific page
     */
    goToPage(page: number): void {
        if (page < 1 || page > this.totalPages) return;
        this.pageIndex = page - 1; // API uses 0-based index
        this.loadInvoices();
    }

    /**
     * Go to next page
     */
    nextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.goToPage(this.currentPage + 1);
        }
    }

    /**
     * Go to previous page
     */
    previousPage(): void {
        if (this.currentPage > 1) {
            this.goToPage(this.currentPage - 1);
        }
    }

    /**
     * Get array of page numbers for pagination UI
     */
    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

        // Adjust start page if we're near the end
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return pages;
    }

    /**
     * Handle page size changes
     */
    onPageSizeChange(size: number): void {
        this.pageSize = size;
        this.pageIndex = 0; // Reset to first page
        this.loadInvoices();
    }
}
